import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Bell } from "lucide-react";
import { api, timeAgo } from "../lib/api";
import { syncAppBadge } from "../lib/pwa";
import { useAuth } from "../context/AuthContext";

export default function NotificationBell() {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const ref = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (!user) return undefined;
    const load = () => api.get("/notifications")
      .then((r) => { setItems(r.data.items); setUnread(r.data.unread); syncAppBadge(r.data.unread); })
      .catch(() => {});
    load();
    const t = setInterval(load, 60000);
    return () => clearInterval(t);
  }, [user]);

  useEffect(() => {
    const close = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, []);

  if (!user) return null;

  const toggle = async () => {
    const next = !open;
    setOpen(next);
    if (next && unread > 0) {
      setUnread(0);
      syncAppBadge(0);
      setItems((list) => list.map((i) => ({ ...i, read: true })));
      api.post("/notifications/read-all").catch(() => {});
    }
  };

  const open_item = (n) => {
    setOpen(false);
    if (n.link) navigate(n.link);
  };

  return (
    <div ref={ref} className="relative">
      <button type="button" onClick={toggle} data-testid="notifications-bell" aria-label="Notifications"
        className="relative p-2 text-zinc-700 hover:text-zinc-950 transition-colors duration-200">
        <Bell className="h-5 w-5" />
        {unread > 0 && (
          <span data-testid="notifications-badge"
            className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 bg-red-600 text-white text-[10px] font-bold rounded-full inline-flex items-center justify-center">
            {unread > 9 ? "9+" : unread}
          </span>
        )}
      </button>
      {open && (
        <div data-testid="notifications-panel"
          className="absolute right-0 mt-2 w-80 max-w-[calc(100vw-2rem)] bg-white border border-zinc-200 rounded-md shadow-xl z-[80] max-h-96 overflow-y-auto">
          <p className="px-4 py-3 text-sm font-semibold text-zinc-950 border-b border-zinc-100">Notifications</p>
          {items.length === 0 ? (
            <p className="px-4 py-6 text-sm text-zinc-500" data-testid="notifications-empty">
              Nothing yet. We'll let you know when something needs your attention.
            </p>
          ) : (
            items.map((n) => (
              <button key={n.id} type="button" onClick={() => open_item(n)} data-testid={`notification-${n.id}`}
                className={`w-full text-left px-4 py-3 border-b border-zinc-50 last:border-0 hover:bg-zinc-50 transition-colors duration-100 ${n.read ? "" : "bg-blue-50/50"}`}>
                <p className="text-sm font-semibold text-zinc-900">{n.title}</p>
                {n.body && <p className="text-xs text-zinc-600 mt-0.5 leading-relaxed">{n.body}</p>}
                <p className="text-[11px] text-zinc-400 mt-1">{timeAgo(n.created_at)}</p>
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
}
